import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { kpiTrackingService } from '../../../services/kpiTrackingService';

const KPIDashboard = () => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadKPIData();

    // Subscribe to real-time updates
    const channel = kpiTrackingService?.subscribeToKPIUpdates((table) => {
      console.log('KPI update from:', table);
      loadKPIData();
    });

    return () => {
      kpiTrackingService?.unsubscribe(channel);
    };
  }, []);

  const loadKPIData = async () => {
    try {
      setLoading(true);
      const data = await kpiTrackingService?.getKPIMetrics();
      setKpiData(data);
    } catch (error) {
      console.error('Error loading KPI data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadKPIData();
  };

  const handleDrillDown = (kpiType) => {
    setSelectedKPI(kpiType);
  };

  const closeDrillDown = () => {
    setSelectedKPI(null);
  };

  const kpiCards = [
    {
      id: 'travelSpend',
      title: 'Travel Spend',
      value: kpiData?.travelSpend?.value || 0,
      change: kpiData?.travelSpend?.change || 0,
      changeType: kpiData?.travelSpend?.changeType || 'neutral',
      icon: 'DollarSign',
      color: 'primary',
      format: 'currency',
      description: 'Total travel expenditure'
    },
    {
      id: 'complianceRates',
      title: 'Compliance Rate',
      value: kpiData?.complianceRates?.value || 0,
      change: kpiData?.complianceRates?.change || 0,
      changeType: kpiData?.complianceRates?.changeType || 'neutral',
      icon: 'Shield',
      color: 'success',
      format: 'percentage',
      description: 'Policy compliance percentage'
    },
    {
      id: 'bookingTrends',
      title: 'Total Bookings',
      value: kpiData?.bookingTrends?.value || 0,
      change: kpiData?.bookingTrends?.change || 0,
      changeType: kpiData?.bookingTrends?.changeType || 'neutral',
      icon: 'TrendingUp',
      color: 'secondary',
      format: 'number',
      description: 'Booking volume trends'
    },
    {
      id: 'budgetUtilization',
      title: 'Budget Utilization',
      value: kpiData?.budgetUtilization?.value || 0,
      change: kpiData?.budgetUtilization?.change || 0,
      changeType: kpiData?.budgetUtilization?.changeType || 'neutral',
      icon: 'PieChart',
      color: 'accent',
      format: 'percentage',
      description: 'Budget usage efficiency'
    },
    {
      id: 'approvalTimes',
      title: 'Avg Approval Time',
      value: kpiData?.approvalTimes?.value || 0,
      change: kpiData?.approvalTimes?.change || 0,
      changeType: kpiData?.approvalTimes?.changeType || 'neutral',
      icon: 'Clock',
      color: 'warning',
      format: 'hours',
      description: 'Average processing time'
    }
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case 'primary': return 'text-primary bg-primary/10 border-primary/20';
      case 'success': return 'text-success bg-success/10 border-success/20';
      case 'warning': return 'text-warning bg-warning/10 border-warning/20';
      case 'error': return 'text-error bg-error/10 border-error/20';
      case 'secondary': return 'text-secondary bg-secondary/10 border-secondary/20';
      case 'accent': return 'text-accent bg-accent/10 border-accent/20';
      default: return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'increase': return 'text-success';
      case 'decrease': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getChangeIcon = (changeType) => {
    switch (changeType) {
      case 'increase': return 'TrendingUp';
      case 'decrease': return 'TrendingDown';
      default: return 'Minus';
    }
  };

  const formatValue = (value, format) => {
    switch (format) {
      case 'currency':
        return `$${value?.toLocaleString()}`;
      case 'percentage':
        return `${value}%`;
      case 'hours':
        return `${value}h`;
      case 'number':
      default:
        return value?.toLocaleString();
    }
  };

  if (loading) {
    return (
      <div className="glass-container rounded-xl border border-border/50 p-8">
        <div className="flex items-center justify-center">
          <Icon name="Loader" size={32} className="animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">Loading KPI metrics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Real-Time KPI Tracking</h2>
          <p className="text-sm text-muted-foreground mt-1">Monitor key performance indicators with drill-down analytics</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName={refreshing ? 'Loader' : 'RefreshCw'}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {kpiCards?.map((kpi) => (
          <div
            key={kpi?.id}
            className="glass-container rounded-xl border border-border/50 p-6 hover:shadow-glass-lg transition-all duration-200 cursor-pointer"
            onClick={() => handleDrillDown(kpi?.id)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${getColorClasses(kpi?.color)}`}>
                <Icon name={kpi?.icon} size={24} />
              </div>
              <div className="flex items-center space-x-1">
                <Icon 
                  name={getChangeIcon(kpi?.changeType)} 
                  size={16} 
                  className={getChangeColor(kpi?.changeType)}
                />
                <span className={`text-sm font-medium ${getChangeColor(kpi?.changeType)}`}>
                  {kpi?.change > 0 ? '+' : ''}{kpi?.change}%
                </span>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-1">
                {formatValue(kpi?.value, kpi?.format)}
              </h3>
              <p className="text-sm text-muted-foreground mb-2">{kpi?.title}</p>
              <p className="text-xs text-muted-foreground/70">{kpi?.description}</p>
            </div>

            <div className="mt-4 pt-4 border-t border-border/30">
              <Button variant="ghost" size="xs" fullWidth iconName="ChevronRight" iconPosition="right">
                View Details
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Drill-Down Modal */}
      {selectedKPI && (
        <DrillDownModal
          kpiType={selectedKPI}
          kpiData={kpiData}
          onClose={closeDrillDown}
        />
      )}
    </div>
  );
};

// Drill-Down Analytics Modal
const DrillDownModal = ({ kpiType, kpiData, onClose }) => {
  const renderContent = () => {
    switch (kpiType) {
      case 'travelSpend':
        return <TravelSpendDrillDown data={kpiData?.travelSpend} />;
      case 'complianceRates':
        return <ComplianceRatesDrillDown data={kpiData?.complianceRates} />;
      case 'bookingTrends':
        return <BookingTrendsDrillDown data={kpiData?.bookingTrends} />;
      case 'budgetUtilization':
        return <BudgetUtilizationDrillDown data={kpiData?.budgetUtilization} />;
      case 'approvalTimes':
        return <ApprovalTimesDrillDown data={kpiData?.approvalTimes} />;
      default:
        return null;
    }
  };

  const getTitleByType = () => {
    const titles = {
      travelSpend: 'Travel Spend Analytics',
      complianceRates: 'Compliance Rate Analytics',
      bookingTrends: 'Booking Trends Analytics',
      budgetUtilization: 'Budget Utilization Analytics',
      approvalTimes: 'Approval Processing Times Analytics'
    };
    return titles?.[kpiType] || 'Analytics';
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-container rounded-2xl border border-border/50 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border/50 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">{getTitleByType()}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

// Travel Spend Drill-Down
const TravelSpendDrillDown = ({ data }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="glass-container p-3 border border-border/50">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-sm text-primary">${payload?.[0]?.value?.toLocaleString()}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Current Month</p>
          <p className="text-2xl font-bold text-foreground">${data?.currentMonth?.toLocaleString()}</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Last Month</p>
          <p className="text-2xl font-bold text-foreground">${data?.lastMonth?.toLocaleString()}</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Total Spend</p>
          <p className="text-2xl font-bold text-foreground">${data?.value?.toLocaleString()}</p>
        </div>
      </div>

      {/* Forecast Chart */}
      {data?.forecast && data?.forecast?.length > 0 && (
        <div className="glass-container p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">3-Month Forecast</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.forecast}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="predicted" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// Compliance Rates Drill-Down
const ComplianceRatesDrillDown = ({ data }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="glass-container p-3 border border-border/50">
          <p className="text-sm font-medium text-foreground">Month {label}</p>
          <p className="text-sm text-success">{payload?.[0]?.value}% Compliant</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Compliant</p>
          <p className="text-2xl font-bold text-success">{data?.compliant}</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Violations</p>
          <p className="text-2xl font-bold text-error">{data?.violations}</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Total Reviewed</p>
          <p className="text-2xl font-bold text-foreground">{data?.total}</p>
        </div>
      </div>

      {/* Trend Chart */}
      {data?.trend && data?.trend?.length > 0 && (
        <div className="glass-container p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">6-Month Compliance Trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="rate" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// Booking Trends Drill-Down
const BookingTrendsDrillDown = ({ data }) => {
  const statusData = [
    { name: 'Confirmed', value: data?.confirmed || 0, color: '#10B981' },
    { name: 'Pending', value: data?.pending || 0, color: '#F59E0B' },
    { name: 'Cancelled', value: data?.cancelled || 0, color: '#EF4444' }
  ];

  const typeData = Object.keys(data?.byType || {})?.map(type => ({
    name: type?.charAt(0)?.toUpperCase() + type?.slice(1),
    value: data?.byType?.[type]
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Confirmed</p>
          <p className="text-2xl font-bold text-success">{data?.confirmed}</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Pending</p>
          <p className="text-2xl font-bold text-warning">{data?.pending}</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Cancelled</p>
          <p className="text-2xl font-bold text-error">{data?.cancelled}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Trend */}
        {data?.weeklyTrend && data?.weeklyTrend?.length > 0 && (
          <div className="glass-container p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-foreground mb-4">Weekly Booking Trend</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.weeklyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="week" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* By Type */}
        {typeData?.length > 0 && (
          <div className="glass-container p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-foreground mb-4">Bookings by Type</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={typeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8B5CF6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Budget Utilization Drill-Down
const BudgetUtilizationDrillDown = ({ data }) => {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Total Budget</p>
          <p className="text-2xl font-bold text-foreground">${data?.totalBudget?.toLocaleString()}</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
          <p className="text-2xl font-bold text-primary">${data?.totalSpent?.toLocaleString()}</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Remaining</p>
          <p className="text-2xl font-bold text-success">${data?.remaining?.toLocaleString()}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        {data?.monthlyTrend && data?.monthlyTrend?.length > 0 && (
          <div className="glass-container p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-foreground mb-4">6-Month Utilization Trend</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data?.monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="utilization" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Forecast */}
        {data?.forecast && data?.forecast?.length > 0 && (
          <div className="glass-container p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-foreground mb-4">3-Month Forecast</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.forecast}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip />
                  <Area type="monotone" dataKey="predicted" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Approval Times Drill-Down
const ApprovalTimesDrillDown = ({ data }) => {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Average Time</p>
          <p className="text-2xl font-bold text-foreground">{data?.value}h</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Fastest</p>
          <p className="text-2xl font-bold text-success">{data?.fastest?.toFixed(1)}h</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Slowest</p>
          <p className="text-2xl font-bold text-error">{data?.slowest?.toFixed(1)}h</p>
        </div>
        <div className="glass-container p-4 rounded-lg">
          <p className="text-sm text-muted-foreground mb-1">Total Processed</p>
          <p className="text-2xl font-bold text-foreground">{data?.total}</p>
        </div>
      </div>

      {/* Trend Chart */}
      {data?.trend && data?.trend?.length > 0 && (
        <div className="glass-container p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">6-Month Processing Time Trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip />
                <Line type="monotone" dataKey="avgTime" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIDashboard;